/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

/**
 * Uploader Service
 *
 * Manages file upload queue, progress tracking, and upload state.
 * Provides a centralized service for handling file uploads to S3 or direct endpoints.
 */

interface UploadFile {
  name: string
  type: string
  progress: (id: number) => string | undefined
}

interface UploadQueue {
  id: number
  files: UploadFile[]
  uploadDef?: {
    promise: Promise<unknown>
  }
  isAborted?: boolean
  isFailed?: boolean
  isCompleted?: boolean
}

interface UploaderState {
  isDisplayed: boolean
  isMinimized: boolean
}

interface AbortController {
  resolve: () => void
}

class UploaderService {
  private readonly UPLOADING_FSI_TYPE = 'DOCUMENT'
  private internalId = 0
  private progresses: Record<number, string> = {}
  private aborts: Record<number, AbortController> = {}

  public state: UploaderState = {
    isDisplayed: false,
    isMinimized: false
  }

  public uploadQueue: UploadQueue[] = []

  constructor() {
    // Listen for logout/login events to clear state
    if (typeof window !== 'undefined') {
      window.addEventListener('logout', () => this.clearFilesAndClose())
      window.addEventListener('login', () => this.clearFilesAndClose())
    }
  }

  /**
   * Clear all uploads and close the uploader
   */
  public clearFilesAndClose(): void {
    this.abortAll()
    this.aborts = {}
    this.progresses = {}
    this.state.isDisplayed = false
    this.uploadQueue = []
  }

  /**
   * Show the uploader UI
   */
  public showLoader(): void {
    if (!this.state.isDisplayed) {
      this.state.isDisplayed = true
    }
  }

  /**
   * Get upload progress for a specific upload ID
   */
  private getProgress(id: number): string | undefined {
    return this.progresses[id]
  }

  /**
   * Set upload progress for a specific upload ID
   * @param id - Upload ID
   * @param progress - Progress percentage (0-100)
   */
  public setProgress(id: number, progress: number): void {
    const progressStr = progress.toFixed(0)
    if (this.progresses[id] !== progressStr) {
      // Use setTimeout to defer the update (matches original $timeout behavior)
      setTimeout(() => {
        this.progresses[id] = progressStr
        // Don't create new array reference here - progress updates are too frequent
        // Progress is read from progresses object, not queue items
      }, 0)
    }
  }

  /**
   * Abort all active uploads
   */
  public abortAll(): void {
    Object.entries(this.aborts).forEach(([key, abort]) => {
      const id = parseInt(key, 10)
      const progress = this.getProgress(id)
      const progressNum = progress ? parseInt(progress, 10) : 0

      if (abort && typeof abort.resolve === 'function' && progressNum < 100) {
        abort.resolve()
        const queue = this.findQueue(id)
        if (queue) {
          queue.isAborted = true
        }
      }
    })
  }

  /**
   * Abort a specific upload by queue ID
   * @param queueId - Queue ID to abort
   */
  public abortQueue(queueId: number): void {
    const abort = this.aborts[queueId]
    const queue = this.findQueue(queueId)
    const progress = this.getProgress(queueId)
    const progressNum = progress ? parseInt(progress, 10) : 0

    if (queue && abort && typeof abort.resolve === 'function' && progressNum < 100) {
      abort.resolve()
      queue.isAborted = true
      // Create new array reference to trigger React updates
      this.uploadQueue = [...this.uploadQueue]
    }
  }

  /**
   * Mark an upload as failed
   * @param queueId - Queue ID to mark as failed
   */
  public setAsFailed(queueId: number): void {
    const queue = this.findQueue(queueId)
    if (queue) {
      queue.isFailed = true
      // Create new array reference to trigger React updates
      this.uploadQueue = [...this.uploadQueue]
    }
  }

  /**
   * Mark an upload as completed
   * @param queueId - Queue ID to mark as completed
   */
  private setAsCompleted(queueId: number): void {
    const queue = this.findQueue(queueId)
    if (queue) {
      queue.isCompleted = true
      // Create new array reference to trigger React updates
      this.uploadQueue = [...this.uploadQueue]
    }
  }

  /**
   * Find a queue by ID
   * @param queueId - Queue ID to find
   */
  private findQueue(queueId: number): UploadQueue | undefined {
    return this.uploadQueue.find((q) => q.id === queueId)
  }

  /**
   * Start a new upload
   * @param files - Array of files to upload
   * @param abort - Abort controller for cancellation
   * @param uploadDef - Upload definition with promise
   * @returns Upload ID
   */
  public startUpload(files: File[], abort: AbortController, uploadDef?: { promise: Promise<unknown> }): number {
    this.showLoader()

    const id = ++this.internalId

    // Create file copies for the queue
    const filesCopy: UploadFile[] = files.map((f) => ({
      name: f.name,
      type: this.UPLOADING_FSI_TYPE,
      progress: (uploadId: number) => this.getProgress(uploadId)
    }))

    this.aborts[id] = abort
    this.setProgress(id, 0)

    const queue: UploadQueue = {
      id,
      files: filesCopy
    }

    // If there's an upload promise, track its completion
    if (uploadDef?.promise) {
      queue.uploadDef = uploadDef

      uploadDef.promise
        .then((result: unknown) => {
          // Check if the result indicates failure (e.g., MigrationResult with success: false)
          // This handles cases where the promise resolves but the operation failed
          if (
            result &&
            typeof result === 'object' &&
            'success' in result &&
            (result as { success: boolean }).success === false
          ) {
            this.setAsFailed(id)
          } else {
            this.setAsCompleted(id)
          }
        })
        .catch((e: { xhrStatus?: string }) => {
          // Only mark as failed if it wasn't aborted
          if (!e || !e.xhrStatus || e.xhrStatus !== 'abort') {
            this.setAsFailed(id)
          }
        })
    }

    // Add to beginning of queue (unshift)
    this.uploadQueue.unshift(queue)

    // Emit event for listeners AFTER adding to queue
    // This ensures listeners see the populated queue
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('uploader.newUploadStarted'))
    }

    return id
  }

  /**
   * Get the current upload queue
   */
  public getUploadQueue(): UploadQueue[] {
    return this.uploadQueue
  }

  /**
   * Get the current uploader state
   */
  public getState(): UploaderState {
    return this.state
  }
}

// Export singleton instance
export const uploaderService = new UploaderService()

// Legacy-compatible export
export const uploader = {
  state: uploaderService.state,
  uploadQueue: uploaderService.uploadQueue,
  startUpload: uploaderService.startUpload.bind(uploaderService),
  showLoader: uploaderService.showLoader.bind(uploaderService),
  setProgress: uploaderService.setProgress.bind(uploaderService),
  clearFilesAndClose: uploaderService.clearFilesAndClose.bind(uploaderService),
  abortAll: uploaderService.abortAll.bind(uploaderService),
  abortQueue: uploaderService.abortQueue.bind(uploaderService),
  setAsFailed: uploaderService.setAsFailed.bind(uploaderService),
  getUploadQueue: uploaderService.getUploadQueue.bind(uploaderService),
  getState: uploaderService.getState.bind(uploaderService)
}

export default uploaderService
